// useBeneAIConfirmation — BeneAI-02.
//
// Resolves a pending confirmation via the BeneAI REST surface,
// commits the resulting receipt message into the transcript, and
// flips the drawer-state row to the canonical post-resolution
// status so the card buttons stay disabled after the user clicks.

import { useCallback } from "react";

import { ApiError } from "@/api/client";

import { cancelConfirmation, confirmConfirmation } from "./api";
import { useBeneAIDrawer } from "./BeneAIDrawerContext";
import type { BeneAIErrorCode } from "./types";


interface ResolveResult {
  ok: boolean;
  error?: { code: BeneAIErrorCode; message: string };
}


export interface UseBeneAIConfirmation {
  confirm: (confirmationId: string) => Promise<ResolveResult>;
  cancel: (confirmationId: string) => Promise<ResolveResult>;
}


export function useBeneAIConfirmation(): UseBeneAIConfirmation {
  const drawer = useBeneAIDrawer();

  const resolve = useCallback(
    async (
      confirmationId: string,
      action: (id: string) => ReturnType<typeof confirmConfirmation>,
    ): Promise<ResolveResult> => {
      drawer.setConfirmationInFlight(confirmationId, true);
      try {
        const resp = await action(confirmationId);
        drawer.setConfirmationStatus(confirmationId, resp.status);
        drawer.appendMessage(resp.assistant_message);
        return { ok: true };
      } catch (err) {
        drawer.setConfirmationInFlight(confirmationId, false);
        return { ok: false, error: parseResolveError(err) };
      }
    },
    [drawer],
  );

  const confirm = useCallback<UseBeneAIConfirmation["confirm"]>(
    (id) => resolve(id, confirmConfirmation),
    [resolve],
  );
  const cancel = useCallback<UseBeneAIConfirmation["cancel"]>(
    (id) => resolve(id, cancelConfirmation),
    [resolve],
  );

  return { confirm, cancel };
}


function parseResolveError(err: unknown): {
  code: BeneAIErrorCode; message: string;
} {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as Record<string, unknown>;
    const detail = typeof body.detail === "string" ? body.detail : null;
    const code = (detail as BeneAIErrorCode) ?? "unknown";
    const message = typeof body.message === "string"
      ? body.message
      : err.message;
    return { code, message };
  }
  if (err instanceof Error) {
    return { code: "unknown", message: err.message };
  }
  return { code: "unknown", message: "Something went wrong." };
}
